import React from "react";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale } from "@insightfulscience/smart-react/i18";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import ProductsTab from "@insightfulscience/atomic-react/ProductsTab";
import { Col, Container, Row } from "@insightfulscience/atomic-react/Grid";
import Section from "@insightfulscience/atomic-react/Section";
import Panel from "@insightfulscience/atomic-react/Panel";
import Heading from "@insightfulscience/atomic-react/Heading";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import Badge from "@insightfulscience/atomic-react/Badge";
import Block from "@insightfulscience/atomic-react/Block";
import Button from "@insightfulscience/atomic-react/Button";
import UList from "@insightfulscience/atomic-react/UList";
import routes from "../../routes";
import WithServices from "../../services";
import withErrorHandling from "../../AppError/withErrorHandling";
import SiteLayout from "../../SiteLayout";
import BiologicsHeader from "../../BiologicsHeader";

const iconPrime = "/images/geneious/logo/icon-prime.svg";
const iconBiologics = "/images/geneious/logo/icon-biologics.svg";

const GetStartedBiologics = () => {
	const [t] = useLocale("get-started-biologics");
	return (
		<SiteLayout isWrapper={false} HeaderComponent={BiologicsHeader}>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.OGP
					locale={t("META.LOCALE")}
					type={t("META.TYPE")}
					title={t("META.TITLE")}
					description={t("META.DESCRIPTION")}
					url={t("META.URL")}
					siteName={t("META.SITE_NAME")}
					articlePublisher={t("META.ARTICLE_PUBLISHER")}
				/>
				<PageMeta.Canonical href={t("META.CANONICAL")} />
				<PageMeta.Twitter
					card={t("META.TWITTER_CARD")}
					title={t("META.TITLE")}
					description={t("META.DESCRIPTION")}
					site={t("META.TWITTER_SITE")}
					creator={t("META.TWITTER_CREATOR")}
				/>
			</PageMeta>
			<Section utility={{ bgColor: "dotmatics-4" }}>
				<Row utility={{ bgColor: "white" }} spacing={{ pt: 9 }}>
					<Col>
						<Container>
							<Row>
								<Col md="auto">
									<ProductsTab
										logoIcon={iconPrime}
										borderColor="3"
										title={t("TAB1.TITLE")}
										description={t("TAB1.DESCRIPTION")}
										href={routes.external.freeTrial}
									/>
								</Col>
								<Col md="auto">
									<ProductsTab
										logoIcon={iconBiologics}
										borderColor="dotmatics-7"
										title={t("TAB2.TITLE")}
										description={t("TAB2.DESCRIPTION")}
										active
									/>
								</Col>
							</Row>
						</Container>
					</Col>
				</Row>
			</Section>
			<Section utility={{ bgColor: "dotmatics-4" }} spacing={{ py: 20 }}>
				<ContentContainer>
					<Row mx my>
						<Col md={6}>
							<Panel spacing={{ py: 8, px: 10 }} className="height-100 position--relative">
								<Badge
									utility={{ bgColor: "dotmatics-7", radius: 0 }}
									className="position-absolute right-0 bottom-100p"
								>
									{t("COL1.BADGE")}
								</Badge>
								<Heading Tag="h2" size="l">
									{t("COL1.TITLE")}
								</Heading>
								<Paragraph sx={{ fontColor: "black--custom-1" }}>{t("COL1.DESCRIPTION")}</Paragraph>
								<Block spacing={{ my: 8 }}>
									<Button.Link href={routes.external.externalSignUp} isBlock type="submit">
										{t("COL1.BTN")}
									</Button.Link>
								</Block>
								<Block spacing={{ mb: 8 }}>
									<Paragraph sx={{ fontWeight: "bold" }}>{t("COL1.LIST_TITLE")}</Paragraph>
									<UList type="pros">
										<UList.Item bulletColor="dotmatics-7">{t("COL1.LIST_1")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL1.LIST_2")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL1.LIST_3")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL1.LIST_4")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL1.LIST_5")}</UList.Item>
									</UList>
								</Block>
							</Panel>
						</Col>
						<Col md={6}>
							<Panel spacing={{ py: 8, px: 10 }} className="height-100">
								<Heading Tag="h2" size="l">
									{t("COL2.TITLE")}
								</Heading>
								<Paragraph sx={{ fontColor: "black--custom-1" }}>{t("COL2.DESCRIPTION")}</Paragraph>
								<Block spacing={{ my: 8 }}>
									<Button.Link href={routes.external.requestDemo} isBlock type="submit">
										{t("COL2.BTN")}
									</Button.Link>
								</Block>
								<Block spacing={{ mb: 8 }}>
									<Paragraph sx={{ fontWeight: "bold" }}>{t("COL2.LIST_TITLE")}</Paragraph>
									<UList type="pros">
										<UList.Item bulletColor="dotmatics-7">{t("COL2.LIST_1")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL2.LIST_2")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL2.LIST_3")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL2.LIST_4")}</UList.Item>
										<UList.Item bulletColor="dotmatics-7">{t("COL2.LIST_5")}</UList.Item>
									</UList>
								</Block>
							</Panel>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
		</SiteLayout>
	);
};

export default pipe(GetStartedBiologics, withErrorHandling, WithServices);
